import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Monitor, 
  Smartphone, 
  Check, 
  Share2,
  MoreVertical,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Desktop Blocker Component
 * 
 * Blocca l'accesso da Desktop/PC mostrando una schermata con:
 * - QR Code Magic Link per auto-login su mobile
 * - Istruzioni per installare PWA
 * - Bottone bypass "Ho già installato l'app"
 * 
 * IMPORTANTE: Appare solo se is_pwa_configured = false nel DB
 * Una volta confermato, non appare MAI più per quell'account
 */
const DesktopBlocker = ({ 
  magicLink, 
  familyName, 
  famigliaId,
  isPwaConfigured,
  supabase,
  onBypass,
  show 
}) => {
  const [bypassClicked, setBypassClicked] = useState(false);
  const [saving, setSaving] = useState(false);

  // Se già configurato nel DB, non mostrare mai
  if (!show || isPwaConfigured) return null;

  const handleBypass = async () => {
    setSaving(true);
    
    try {
      // Salva nel DATABASE tramite API backend (bypassa RLS)
      if (famigliaId) {
        console.log('Tentativo salvataggio PWA configured per famiglia:', famigliaId);
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/pwa/set-configured`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ famiglia_id: famigliaId })
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('is_pwa_configured salvato nel DB');
        } else {
          console.error('Errore salvataggio is_pwa_configured:', result.error);
        }
      } else {
        console.warn('famigliaId non disponibile, skip salvataggio DB');
      }
      
      // Backup in localStorage (per sessione corrente) - SEMPRE
      localStorage.setItem('desktop_blocker_bypassed', 'true');
      
      // Setta come completato
      setBypassClicked(true);
      
      // Callback per sbloccare la dashboard
      if (onBypass) onBypass();
      
    } catch (err) {
      console.error('Errore bypass:', err);
      // Anche in caso di errore, sblocca comunque via localStorage
      localStorage.setItem('desktop_blocker_bypassed', 'true');
      setBypassClicked(true);
      if (onBypass) onBypass();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#1B4332] via-[#2D6A4F] to-[#40916C] flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full space-y-5 my-4">
        {/* Header con icona warning */}
        <div className="text-center space-y-3">
          <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border-2 border-white/20">
            <Monitor className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            Attenzione
          </h1>
          <p className="text-white/90 text-base leading-relaxed">
            Per ricevere gli <strong>avvisi sulla terapia in tempo reale</strong> (notifiche push), 
            devi attivare CuraCheck sul tuo <strong>smartphone</strong>.
          </p>
        </div>

        {/* Card principale con QR */}
        <div className="bg-white rounded-3xl p-6 shadow-2xl space-y-5">
          {/* Titolo card */}
          <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
            <div className="w-10 h-10 bg-[#4A7C59] rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-[#2C3E50]">Scansiona con il telefono</h2>
              <p className="text-xs text-[#64748B]">Accesso automatico senza password</p>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex justify-center py-4">
            <div className="p-4 bg-white border-4 border-[#4A7C59] rounded-2xl shadow-lg">
              <QRCodeSVG 
                value={magicLink} 
                size={180}
                level="H"
                includeMargin={false}
                fgColor="#1B4332"
                imageSettings={{
                  src: "/logo-curacheck.png",
                  height: 35,
                  width: 35,
                  excavate: true
                }}
              />
            </div>
          </div>

          {/* Istruzioni rapide */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-[#2C3E50] text-center">
              Dopo la scansione, installa l'app:
            </p>
            
            {/* iOS */}
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-xl p-3">
              <div className="w-9 h-9 bg-[#007AFF] rounded-lg flex items-center justify-center flex-shrink-0">
                <Share2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#2C3E50]">iPhone / iPad</p>
                <p className="text-xs text-[#64748B]">
                  Tocca <span className="font-medium">Condividi</span> → <span className="font-medium">"Aggiungi alla Home"</span>
                </p>
              </div>
            </div>

            {/* Android */}
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-xl p-3">
              <div className="w-9 h-9 bg-[#3DDC84] rounded-lg flex items-center justify-center flex-shrink-0">
                <MoreVertical className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#2C3E50]">Android</p>
                <p className="text-xs text-[#64748B]">
                  Tocca <span className="font-medium">⋮ Menu</span> → <span className="font-medium">"Aggiungi a schermata Home"</span>
                </p>
              </div>
            </div>
            
            {/* Troubleshooting tip */}
            <div className="bg-[#EFF6FF] rounded-xl p-3 mt-2">
              <p className="text-xs font-semibold text-[#1E40AF] mb-1">💡 Problemi con "Famiglia Non Trovata"?</p>
              <p className="text-xs text-[#3B82F6]">
                Cancella la cache/dati del browser e riprova.
              </p>
              <p className="text-xs text-[#3B82F6] mt-1">
                <span className="font-medium">iPhone:</span> usa Safari o Chrome
              </p>
              <p className="text-xs text-[#3B82F6]">
                <span className="font-medium">Android:</span> usa l'app Chrome (consigliato)
              </p>
            </div>
          </div>

          {/* Perché è importante */}
          <div className="bg-[#FEF3C7] rounded-xl p-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-[#92400E]">Perché devo usare il telefono?</p>
              <p className="text-xs text-[#B45309] mt-1">
                Le notifiche push funzionano solo su smartphone. Senza l'app installata, 
                non riceverai avvisi quando l'assistente registra le attività.
              </p>
            </div>
          </div>
        </div>

        {/* Bypass button - BEN VISIBILE */}
        <div className="text-center py-4">
          <button
            onClick={handleBypass}
            disabled={bypassClicked || saving}
            className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white text-base font-semibold rounded-xl 
                       transition-all border-2 border-white/40 hover:border-white/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="desktop-blocker-bypass-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2 justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Salvataggio...
              </span>
            ) : bypassClicked ? (
              <span className="flex items-center gap-2 justify-center">
                <Check className="w-5 h-5" />
                Configurazione completata!
              </span>
            ) : (
              "Sì, ho già installato l'app sul telefono →"
            )}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center pt-2">
          <img 
            src="/logo-curacheck.png" 
            alt="CuraCheck" 
            className="h-8 opacity-80"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Hook per rilevare se l'utente è su Desktop
 * NON controlla più localStorage - la persistenza è nel DB
 */
export const useDesktopDetection = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check se è in modalità standalone (PWA installata)
    const standalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Detect se è desktop basandosi su:
    // 1. Screen width > 1024px
    // 2. No touch support
    // 3. User agent non contiene mobile keywords
    const ua = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);
    const isLargeScreen = window.innerWidth > 1024;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // È desktop se:
    // - Screen grande E non ha touch E non è mobile UA
    // OPPURE
    // - È esplicitamente un desktop browser (Windows, Mac, Linux non mobile)
    const isDesktopOS = /windows|macintosh|linux/i.test(ua) && !isMobileUA;
    
    const desktop = (isLargeScreen && !hasTouch) || (isDesktopOS && !isMobileUA);
    
    console.log('[DesktopDetection]', { 
      isDesktop: desktop, 
      isStandalone: standalone, 
      screenWidth: window.innerWidth,
      hasTouch,
      isMobileUA 
    });
    
    setIsDesktop(desktop);
  }, []);

  return {
    isDesktop,
    isStandalone,
    // shouldBlock ora dipende SOLO da isDesktop e isStandalone
    // La verifica is_pwa_configured viene fatta nel componente padre
    shouldBlock: isDesktop && !isStandalone
  };
};

export default DesktopBlocker;
